/**
 * Header/card buttons for the Drilldown Tab Header Standard (§6).
 *
 * Two shapes, and only two:
 *   • TabPrimaryButton — the tab's single primary action. Accent fill, white
 *     label. One per tab, maximum.
 *   • TabGhostButton   — secondary page actions and every card-scoped control.
 *     Surface fill, strong-border outline, ink-2 label.
 *
 * Both set background AND color together — never one without the other, or the
 * theme's navy default collides with an overridden text color.
 *
 * Label copy: active voice, names the outcome. `Add property`, `Run review`,
 * `Export file`. Never `Submit`, `Go`, `Manage`. The label stays constant through
 * the flow — the button, the panel it opens, and the toast it fires all agree.
 */
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import PropTypes from "prop-types";
import { memo } from "react";

import { TAB_TOKENS as T } from "./tabTokens";

const BASE = {
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 600,
  borderRadius: T.radius.xs,
  boxShadow: "none",
  whiteSpace: "nowrap",
  minWidth: 0,
  "&:focus-visible": {
    outline: `2px solid ${T.color.brand}`,
    outlineOffset: "2px",
  },
  "& .MuiButton-startIcon": { marginRight: "6px" },
  "& .MuiButton-startIcon svg": { fontSize: 16 },
};

export const TabPrimaryButton = memo(
  ({ label, children, icon, loading = false, disabled, onClick, sx, ...rest }) => (
    <Button
      variant="contained"
      disableElevation
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={13} thickness={5} sx={{ color: "inherit" }} />
        ) : (
          icon || undefined
        )
      }
      sx={{
        ...BASE,
        padding: "9px 14px",
        background: T.color.accent,
        color: "#fff",
        "&:hover": { background: "#0f172a", boxShadow: "none" },
        "&.Mui-disabled": {
          background: "#e2e8f0",
          color: T.color.muted,
        },
        ...sx,
      }}
      {...rest}
    >
      {label || children}
    </Button>
  ),
);

export const TabGhostButton = memo(
  ({
    label,
    children,
    icon,
    loading = false,
    disabled,
    onClick,
    size = "md",
    sx,
    ...rest
  }) => (
    <Button
      variant="outlined"
      disableElevation
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={13} thickness={5} sx={{ color: "inherit" }} />
        ) : (
          icon || undefined
        )
      }
      sx={{
        ...BASE,
        fontSize: size === "sm" ? "11.5px" : "12.5px",
        padding: size === "sm" ? "6px 10px" : "8px 13px",
        background: T.color.surface,
        color: T.color.ink2,
        border: `1px solid ${T.color.borderStrong}`,
        "&:hover": {
          background: T.color.surfaceSunk,
          borderColor: T.color.ink3,
          color: T.color.ink,
        },
        "&.Mui-disabled": {
          background: T.color.surface,
          color: T.color.muted,
          borderColor: T.color.border,
        },
        ...sx,
      }}
      {...rest}
    >
      {label || children}
    </Button>
  ),
);

const actionShape = {
  label: PropTypes.node,
  children: PropTypes.node,
  icon: PropTypes.node,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  sx: PropTypes.object,
};

TabPrimaryButton.propTypes = actionShape;
TabGhostButton.propTypes = { ...actionShape, size: PropTypes.oneOf(["sm", "md"]) };

TabPrimaryButton.displayName = "TabPrimaryButton";
TabGhostButton.displayName = "TabGhostButton";
